import { useState } from 'react'

import * as Sentry from '@sentry/react'
import { Typography } from 'antd'
import GooglePlacesAutocomplete, {
  getLatLng,
  geocodeByAddress,
} from 'react-google-places-autocomplete'
import GooglePlacesAutocompleteProps, {
  LatLng,
} from 'react-google-places-autocomplete/build/GooglePlacesAutocomplete.types'
import styled from 'styled-components'

import { Input } from '../shared-components/BUI/Input'

import config from '@/config/index'
import { useLocale } from '@/context/localeContext'
import { getDataTags } from '@/utils/index'

import { buildPassTheme } from 'src/styles/theme'

const fetchTimezone = async (
  lat: number,
  lng: number
): Promise<string | null> => {
  try {
    const timestamp = Math.floor(Date.now() / 1000)
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${config.googleMapsAPIKey}`
    )
    const data = await response.json()

    if (data.status !== 'OK') {
      Sentry.captureException(
        new Error('Error fetching timezone from Google Maps:', data.status)
      )

      return null
    }

    return data.timeZoneId
  } catch (error) {
    Sentry.captureException(
      new Error('Error fetching timezone from Google Maps:', error)
    )
    return null
  }
}

const NoResults = styled(Typography.Text).attrs({ type: 'secondary' })`
  display: block;
  text-align: center;
  padding: 0.5rem;
`

const GoManual = styled.span`
  text-decoration: underline;
  cursor: pointer;
`

type Selection = {
  label: string
}

export type AddressObject = {
  label: string
}

type Props = {
  value?: string | Selection
  onChange?: (value: string | AddressObject) => void
  getValue?: () => string
  placeholder?: string
  styles?: GooglePlacesAutocompleteProps['selectProps']['styles']
  useLabelAsValue?: boolean
  geoCodeCallback?: (value: LatLng) => void
  timezoneCallback?: (value: string) => void
  localeCallback?: (value: string) => void
}

const AddressAutocomplete: React.FC<Props> = ({
  value: formValue,
  getValue,
  placeholder,
  onChange,
  styles,
  useLabelAsValue,
  geoCodeCallback,
  timezoneCallback,
  localeCallback,
  ...formItemProps
}) => {
  const { tLanguage, countryCode } = useLocale()
  const [manualInput, setManualInput] = useState(false)

  const autoValue = typeof formValue === 'string' ? formValue : formValue?.label

  const onChangeLabel = (value: AddressObject) => {
    onChange?.(value.label)
  }

  const hasChanged = async (value: AddressObject) => {
    const geocode = await geocodeByAddress(value.label)
    const latLng = await getLatLng(geocode?.[0])
    const timezone = await fetchTimezone(latLng.lat, latLng.lng)

    localeCallback?.(
      geocode?.[0].address_components?.find((component) =>
        component.types.includes('country')
      )?.short_name
    )
    timezoneCallback?.(timezone || '')
    geoCodeCallback?.(latLng)
    onChange?.(value)
  }

  const goManual = (manualValue: string) => {
    setManualInput(true)
    saveManualAddress(manualValue)
  }

  const saveManualAddress = (manualValue: string) => {
    const formattedValue: AddressObject = { label: manualValue }
    useLabelAsValue ? onChangeLabel(formattedValue) : onChange(formattedValue)
  }

  const onManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    saveManualAddress(e.target.value)
  }

  const value = autoValue || getValue?.()

  if (manualInput) {
    return (
      <Input
        value={value}
        onChange={onManualInputChange}
        placeholder={placeholder}
        {...getDataTags({ name: 'manual-address-input', type: 'input' })}
      />
    )
  }

  return (
    <GooglePlacesAutocomplete
      apiKey={config.googleMapsAPIKey}
      apiOptions={{ language: tLanguage, region: countryCode }}
      selectProps={{
        ...formItemProps,
        value: value
          ? {
              label: value,
              value: value,
            }
          : null,
        onChange: useLabelAsValue ? onChangeLabel : hasChanged,
        components: {
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
          NoOptionsMessage: ({ selectProps }) => {
            const currentInputValue = selectProps?.inputValue || ''

            return (
              <NoResults>
                Can&apos;t find the correct address?{' '}
                <GoManual onClick={() => goManual(currentInputValue)}>
                  Enter manually
                </GoManual>
              </NoResults>
            )
          },
        },
        placeholder,
        styles: {
          control: (provided) => ({
            ...provided,
            border: `1px solid ${buildPassTheme.colour.secondary._400}`,
            paddingLeft: '6px',
            paddingRight: '18px',
            paddingBottom: '6px',
            paddingTop: '6px',
            borderRadius: '4px',
            ...styles?.control,
          }),
          valueContainer: (provided) => ({
            ...provided,
            padding: 0,
            ...styles?.valueContainer,
            cursor: 'text',
          }),
          input: (provided) => ({
            ...provided,
            fontSize: '14px !important',
            ...styles?.input,
          }),
          option: (provided) => ({
            ...provided,
            fontSize: '14px !important',
            ...styles?.option,
          }),
          singleValue: (provided) => ({
            ...provided,
            fontSize: '14px !important',
            ...styles?.singleValue,
          }),
          placeholder: (provided) => ({
            ...provided,
            fontSize: '14px !important',
            color: buildPassTheme.colour.secondary._400,
            ...styles?.placeholder,
          }),
          container: (provided) => ({
            ...provided,
            zIndex: 1010,
          }),
        },
      }}
    />
  )
}

export default AddressAutocomplete
